import { ReactNode, useCallback, useMemo, useRef, useState } from 'react'

import { ConfirmationContext, ShowConfirmationDialogParams } from './context'
import { ConfirmationDialog } from './ConfirmationDialog'

type DialogState = {
  open: boolean
  title: ReactNode
  message: ReactNode
  onConfirm: () => any
  onCancel: () => any
  datePicker?: boolean
  datePickerAttributes?: ShowConfirmationDialogParams['datePickerAttributes']
}

export function ConfirmationDialogProvider({
  children
}: {
  children: ReactNode
}) {
  const [dialog, setDialog] = useState<DialogState>({
    open: false,
    title: null,
    message: null,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onConfirm: () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onCancel: () => {}
  })

  // pickerDate is managed separately so it can be updated without re-opening the dialog
  const [pickerDate, setPickerDate] = useState<Date>(new Date())
  // Store onDateChange in a ref to always call the latest version without stale closures
  const onDateChangeRef = useRef<((val: any) => any) | undefined>(undefined)

  const closeDialog = useCallback(() => {
    setDialog((prev) => ({
      ...prev,
      open: false,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onConfirm: () => {},
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onCancel: () => {}
    }))
  }, [])

  const showConfirmationDialog = useCallback(
    (params: ShowConfirmationDialogParams) => {
      onDateChangeRef.current = params.onDateChange

      if (params.date != null) {
        setPickerDate(params.date)
      }

      const onConfirm = () => {
        params.onConfirm()
        closeDialog()
      }

      const onCancel = () => {
        if (params.onCancel) params.onCancel()
        closeDialog()
      }

      setDialog({
        open: true,
        title: params.title,
        message: params.message,
        onConfirm,
        onCancel,
        datePicker: params.datePicker,
        datePickerAttributes: params.datePickerAttributes
          ? {
              minDate: params.datePickerAttributes.minDate,
              maxDate: params.datePickerAttributes.maxDate,
              filterDate: params.datePickerAttributes.filterDate
            }
          : undefined
      })
    },
    [closeDialog]
  )

  // Called by ConfirmationDialog when user picks a date; delegates to the caller's
  // onDateChange (which may snap/transform the value) and updates pickerDate directly.
  const handleDateChange = useCallback((val: any) => {
    const result = onDateChangeRef.current?.(val)
    const newDate =
      result instanceof Date
        ? result
        : val instanceof Date
        ? val
        : new Date(val)
    setPickerDate(newDate)
  }, [])

  const value = useMemo(
    () => ({ showConfirmationDialog }),
    [showConfirmationDialog]
  )

  return (
    <ConfirmationContext.Provider value={value}>
      {children}
      <ConfirmationDialog
        {...dialog}
        date={pickerDate}
        onDateChange={handleDateChange}
      />
    </ConfirmationContext.Provider>
  )
}
